// ── Shared format utilities ───────────────────────────────────────────────────

import type { FindingCategory } from '@/lib/apiTypes'

/** The registry base host. Provider sources are org-scoped by subdomain:
 *  `<slug>.registry.terramantle.dev`. (Modules stay slug-in-path on the bare host.) */
export const REGISTRY_BASE_HOST = 'registry.terramantle.dev'

/** The org-scoped provider registry host for a given org slug. Falls back to a
 *  `<org-slug>` placeholder when no org is selected so guide snippets still render. */
export function providerRegistryHost(slug: string | null | undefined): string {
  return `${slug || '<org-slug>'}.${REGISTRY_BASE_HOST}`
}

/** Terraform's per-host token env var for a registry host. tofu's encoding is NOT
 *  "all non-alphanumerics → `_`": a dot becomes a single `_` and a hyphen becomes a
 *  double `__` (env var names can't contain hyphens), so a hyphenated org slug
 *  resolves correctly — `my-org.registry.terramantle.dev` ->
 *  `TF_TOKEN_my__org_registry_terramantle_dev`. tofu authenticates to the FULL
 *  subdomain host, so the var is keyed on it. */
export function providerTokenEnvVar(host: string): string {
  return `TF_TOKEN_${host.replace(/-/g, '__').replace(/\./g, '_')}`
}

/**
 * Human display labels for the tool-agnostic finding categories. The UI must
 * only ever surface these abstract labels, never the underlying scanner tool.
 */
export const CATEGORY_LABELS: Record<FindingCategory, string> = {
  secret:           'Secrets',
  misconfiguration: 'Misconfigurations',
  malware:          'Malware',
  exposure:         'Exposure',
  vulnerability:    'Vulnerabilities',
}

/** External advisory page for a vulnerability id: NVD for CVE-*, GitHub Advisories for GHSA-*,
 *  OSV for anything else (GO-*, PYSEC-*, ...). Null for an empty/unknown id. */
export function advisoryUrl(id: string | null | undefined): string | null {
  const v = (id ?? '').trim()
  if (!v) return null
  if (/^CVE-\d{4}-\d+$/i.test(v)) return `https://nvd.nist.gov/vuln/detail/${v.toUpperCase()}`
  if (/^GHSA-/i.test(v)) return `https://github.com/advisories/${v.toUpperCase()}`
  return `https://osv.dev/vulnerability/${encodeURIComponent(v)}`
}

export const SEVERITY_COLOR: Record<string, string> = {
  critical: 'var(--red)',
  high:     'var(--amber)',
  medium:   'var(--yellow)',
  low:      'var(--blue)',
  info:     'var(--sev-info)',
}

export const SEVERITY_RANK: Record<string, number> = {
  critical: 5,
  high:     4,
  medium:   3,
  low:      2,
  info:     1,
}

export function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatDateTime(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/** Date + time to the second - used for hover tooltips that want the exact moment. */
export function formatDateTimeSeconds(ts: number): string {
  return new Date(ts).toLocaleString('en-GB', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

export function relativeTime(ms: number): string {
  const diff = Date.now() - ms
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

export function formatCount(n: number | null | undefined): string {
  // Guard null/NaN so a partial/malformed API payload renders "-" rather than
  // "NaN" or throwing a TypeError (null has no .toLocaleString).
  if (n == null || Number.isNaN(n)) return '-'
  return n.toLocaleString('en-GB')
}

/** Human byte size in binary units: "0 B", "4.2 KiB", "1.5 MiB", "3.1 GiB". null/undefined/NaN -> "-". */
export function formatBytes(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n) || n < 0) return '-'
  if (n < 1024) return `${n} B`
  const units = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB']
  let val = n / 1024
  let i = 0
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024
    i++
  }
  return `${val >= 100 ? Math.round(val) : val.toFixed(1)} ${units[i]}`
}

/** Compact wall-clock duration: "820ms", "4.2s", "3m 5s", "1h 2m". null/negative -> "-". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || ms < 0) return '-'
  if (ms < 1000) return `${Math.round(ms)}ms`
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return `${(ms / 1000).toFixed(1)}s`
  const mins = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  if (mins < 60) return `${mins}m ${sec}s`
  const hrs = Math.floor(mins / 60)
  return `${hrs}h ${mins % 60}m`
}

/**
 * Resolves what the merged "Pushed by" column should draw for a state version:
 * which icon family to use and a human label. kind 'github'/'gitlab' -> the VCS
 * provider logo (RepoProviderIcon, clickable to the commit); 'human'/'bot'/'unknown'
 * -> an ActorIcon. Mirrors the actor classification stored at push time (deriveActor
 * in the worker). Display-only.
 */
export function pushedByLabel(
  actorType: string | null,
  vcsProvider?: string | null,
): { kind: 'github' | 'gitlab' | 'human' | 'bot' | 'unknown'; label: string } {
  if (!actorType) return { kind: 'unknown', label: 'Unknown / legacy' }
  if (actorType === 'vcs') {
    if (vcsProvider === 'github') return { kind: 'github', label: 'GitHub Actions' }
    if (vcsProvider === 'gitlab') return { kind: 'gitlab', label: 'GitLab CI' }
    return { kind: 'unknown', label: 'VCS OIDC' }
  }
  if (actorType === 'bot_token') return { kind: 'bot', label: 'Service account' }
  if (actorType === 'human') return { kind: 'human', label: 'Human' }
  return { kind: 'unknown', label: actorType }
}
